import { useState, useEffect } from "react";
import {
  Table,
  Button,
  Row,
  Col,
  Card,
  Spin,
  Input,
  Modal,
  message,
  Tooltip,
} from "antd";
import {
  FolderOutlined,
  SearchOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { ColumnType } from "antd/es/table";
import { FaPen, FaTrashAlt } from "react-icons/fa";
import { RiAddBoxFill } from "react-icons/ri";
import AmcMaintenanceModal from "../AMC-Maintenance/Add";
import { getAllAmc, deleteAmc } from "../../utils/API";
import { autoRegisterCurrentPageColumns } from "../../utils/emailTemplateColumns";

interface AmcRecord {
  _id: string;
  project_name:
    | string
    | {
        _id: string;
        projectName: string;
      };
  customer_name:
    | string
    | {
        _id: string;
        name: string;
      };
  customer_contact_number: string;
  service_type: string;
  start_date: string;
  end_date: string;
}

const AmcMaintenancePage = () => {
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [dataSource, setDataSource] = useState<AmcRecord[]>([]);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editData, setEditData] = useState<AmcRecord | null>(null);
  const [placeholderText, setPlaceholderText] = useState("");

  const getProjectName = (project: AmcRecord["project_name"]) => {
    if (typeof project === "string") return project || "-";
    return project?.projectName || "-";
  };

  const getCustomerName = (customer: AmcRecord["customer_name"]) => {
    if (typeof customer === "string") return customer || "-";
    return customer?.name || "-";
  };

  // ===== FETCH DATA (Now accepts search text) =====
  const fetchAmcData = async (searchQuery = "") => {
    try {
      setLoading(true);
      // Pass the query to your backend
      const response: any = await getAllAmc(searchQuery);
      setDataSource(response || []);
    } catch (error) {
      message.error("Failed to fetch AMC records");
    } finally {
      setLoading(false);
    }
  };

  // ===== SEARCH EFFECT (Server-side with Debounce) =====
  useEffect(() => {
    // Wait 400ms after user stops typing before calling the API
    const delayDebounceFn = setTimeout(() => {
      fetchAmcData(searchText);
    }, 400);

    // Cleanup timeout if user types again before 400ms is up
    return () => clearTimeout(delayDebounceFn);
  }, [searchText]);

  // ===== ADD =====
  const handleOpenAddModal = () => {
    setEditData(null);
    setIsModalOpen(true);
  };

  // ===== EDIT =====
  const handleOpenEditModal = (record: AmcRecord) => {
    setEditData(record);
    setIsModalOpen(true);
  };

  // ===== DELETE =====
  const handleDelete = async () => {
    try {
      if (!deleteId) return;
      await deleteAmc(deleteId);
      message.success("AMC record deleted successfully");
      fetchAmcData(searchText); // Refresh with current search text
    } catch {
      message.error("Delete failed");
    } finally {
      setDeleteId(null);
    }
  };

  // ===== MODAL SUCCESS =====
  const handleModalSuccess = () => {
    setIsModalOpen(false);
    setEditData(null);
    fetchAmcData(searchText); // Refresh with current search text
  };

  // ===== NEW TABLE INDEX =====
  // ===== NEW TABLE INDEX =====
  const columns: ColumnType<AmcRecord>[] = [
    {
      title: "#",
      width: 60,
      align: "center",
      fixed: "left" as const,
      render: (_, __, index) => (
        <span style={{ fontWeight: 500, color: "#4B5563" }}>{index + 1}</span>
      ),
    },
    {
      title: "Project Name",
      fixed: "left" as const,
      render: (_, record) => {
        const projectName = getProjectName(record.project_name);
        if (projectName === "-") {
          return <span style={{ color: "#9CA3AF" }}>-</span>;
        }
        return (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <FolderOutlined style={{ color: "#3B82F6", fontSize: "16px" }} />
            <span style={{ fontWeight: 500, color: "#1F2937" }}>
              {projectName}
            </span>
          </div>
        );
      },
    },
    {
      title: "Customer Name",
      render: (_, record) => {
        const customerName = getCustomerName(record.customer_name);
        if (customerName === "-") {
          return <span style={{ color: "#9CA3AF" }}>-</span>;
        }
        return (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <UserOutlined style={{ color: "#6B7280", fontSize: "14px" }} />
            <span style={{ fontWeight: 500, color: "#1F2937" }}>
              {customerName}
            </span>
          </div>
        );
      },
    },
    {
      title: "Contact Number",
      dataIndex: "customer_contact_number",
      key: "customer_contact_number",
      render: (text) => (
        <span style={{ fontWeight: 500, color: "#4B5563" }}>{text || "-"}</span>
      ),
    },
    {
      title: "Service Type",
      dataIndex: "service_type",
      key: "service_type",
      render: (text) => {
        const type = text || "-";

        if (type === "-") return <span style={{ color: "#9CA3AF" }}>-</span>;

        // Default Gray
        let bgColor = "#F8FAFC";
        let textColor = "#4B5563";
        let borderColor = "#E2E8F0";

        const lowerType = type.toLowerCase();

        // 1. Hosting / Domain colors (Matches your uploaded image)
        if (lowerType.includes("domain")) {
          bgColor = "#F4F6F8"; // Very light grayish-blue
          textColor = "#1D4ED8"; // Dark Blue text
          borderColor = "#E2E8F0";
        }
        // 2. Add your second service type condition here (e.g., "maintenance", "development")
        else if (lowerType.includes("hosting")) {
          bgColor = "#FFFBEB"; // Light Amber
          textColor = "#D97706"; // Dark Amber
          borderColor = "#FDE68A";
        }

        return (
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: bgColor,
              color: textColor,
              border: `1px solid ${borderColor}`,
              padding: "4px 12px",
              borderRadius: "16px",
              fontWeight: 500,
              fontSize: "13px",
            }}
          >
            {type}
          </div>
        );
      },
    },
    {
      title: "Start Date",
      dataIndex: "start_date",
      key: "start_date",
    },
    {
      title: "End Date",
      dataIndex: "end_date",
      key: "end_date",
    },
    {
      title: "Action",
      align: "center",
      width: 140,
      render: (_, record) => (
        <div className="flex justify-center gap-2">
          {/* EDIT BUTTON */}
          <Tooltip title="Edit">
            <button
              onClick={() => handleOpenEditModal(record)}
              className="w-7 h-7 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 
                          shadow-[0_4px_12px_rgba(139,92,246,0.3)] 
                          hover:shadow-[0_6px_16px_rgba(139,92,246,0.4)] 
                          active:translate-y-[1px] active:shadow-none 
                          text-white flex items-center justify-center 
                          transition-all duration-200 ease-in-out group"
            >
              <FaPen
                className="group-hover:scale-110 transition-transform"
                size={11}
              />
            </button>
          </Tooltip>

          {/* DELETE BUTTON */}
          <Tooltip title="Delete">
            <button
              onClick={() => setDeleteId(record._id)}
              className="w-7 h-7 rounded-full bg-gradient-to-br from-rose-500 to-rose-700
              shadow-[0_4px_12px_rgba(244,63,94,0.3)]
              hover:shadow-[0_6px_16px_rgba(244,63,94,0.4)]
              active:translate-y-1 active:shadow-none
              text-white flex items-center justify-center
              transition-all duration-200 group"
            >
              <FaTrashAlt
                className="group-hover:scale-110 transition-transform"
                size={11}
              />
            </button>
          </Tooltip>
        </div>
      ),
    },
  ];

  // useEffect(() => {
  //   const text = "Search project or customer...";
  //   let currentIndex = 0;
  //   let isTyping = true;

  //   const interval = setInterval(() => {
  //     if (isTyping) {
  //       setPlaceholderText(text.slice(0, currentIndex + 1));
  //       currentIndex++;
  //       if (currentIndex === text.length) isTyping = false; // Pause at the end
  //     } else {
  //       setPlaceholderText(text.slice(0, currentIndex - 1));
  //       currentIndex--;
  //       if (currentIndex === 0) isTyping = true; // Start typing again
  //     }
  //   }, 150); // Speed of the typing

  //   return () => clearInterval(interval);
  // }, []);

  useEffect(() => {
    autoRegisterCurrentPageColumns(columns);
  }, []);

  return (
    <div className="p-2 bg-gray-100 min-h-screen">
      {/* HEADER */}
      <Row className="m-2" align="middle">
        <Col xs={24} md={8}>
          <h2 style={{ margin: 0, fontWeight: 600, fontSize: 20 }}>
            AMC Maintenance
          </h2>
        </Col>

        <Col xs={24} md={16}>
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 10,
            }}
          >
            <Input
              prefix={<SearchOutlined />}
              placeholder={"Search project or customer "} // ⭐ USE THE STATE HERE
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 240 }}
              allowClear
            />

            <Button
              type="primary"
              onClick={handleOpenAddModal}
              style={{ background: "#001529", height: "33px" }}
            >
              <RiAddBoxFill /> Add
            </Button>
          </div>
        </Col>
      </Row>

      {/* TABLE */}
      <Card>
        <Spin spinning={loading}>
          {/* ⭐ WRAPPER DIV AND SCROLL PROPERTY ADDED HERE ⭐ */}
          <div
            className="scrollbar-hide custom-scroll-table"
            style={{ overflowX: "auto" }}
          >
            <Table
              scroll={{ x: "max-content" }}
              columns={columns}
              // We now pass dataSource directly because filtering is done on the server
              dataSource={dataSource}
              rowKey="_id"
              pagination={{ pageSize: 10 }}
            />
          </div>
        </Spin>
      </Card>

      {/* MODAL */}
      <AmcMaintenanceModal
        visible={isModalOpen}
        editData={editData}
        onClose={() => setIsModalOpen(false)}
        onSuccess={handleModalSuccess}
      />

      {/* DELETE MODAL */}
      <Modal
        open={!!deleteId}
        onOk={handleDelete}
        onCancel={() => setDeleteId(null)}
        okText="Yes"
        cancelText="No"
        width={500}
        centered={false}
        destroyOnClose
        className="custom-modal"
        style={{
          bottom: 20,
          top: "10vw",
          maxHeight: "calc(100vh - 30px)",
          margin: "0 auto",
          position: "relative",
        }}
        title={
          <div
            style={{
              marginLeft: "11px",
              width: "100%",
              paddingRight: 20,
            }}
          >
            Confirmation
          </div>
        }
        okButtonProps={{
          style: {
            backgroundColor: "#ff4d4f",
            borderColor: "#ff4d4f",
            color: "white",
          },
        }}
        bodyStyle={{
          padding: "22px",
          overflowY: "auto",
          fontFamily: "inter, sans-serif",
        }}
        styles={{
          header: {
            borderBottom: "1px solid #e0e0e0",
            padding: "12px",
            position: "sticky",
            top: 0,
            backgroundColor: "white",
          },
          content: {
            padding: 0,
            overflow: "hidden",
          },
          footer: {
            borderTop: "1px solid #e0e0e0",
            padding: "12px",
            position: "sticky",
            top: 0,
            backgroundColor: "white",
          },
        }}
      >
        <p>Are you sure you want to delete this AMC record?</p>
      </Modal>
    </div>
  );
};

export const AMC_TABLE_COLUMNS = [
  "projectName",
  "customerName",
  "customer_contact_number",
  "service_type",
  "start_date",
  "end_date",
];

export default AmcMaintenancePage;
